import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, RotateCcw, ShieldOff, X, KeyRound } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface SystemUser {
  uid: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  lastLogin?: string;
  createdAt: string;
  suspendedAt?: string;
}

export function UserManagementPage() {
  const { get, post } = useApi();
  const { currentUser, userRole } = useAuth();
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ displayName: '', email: '', temporaryPassword: '' });

  const fetchUsers = useCallback(async () => {
    const result = await get<SystemUser[]>('/users');
    if (result.success && result.data) setUsers(result.data);
    else setError(result.error || 'Failed to load users');
  }, [get]);

  useEffect(() => { setLoading(true); fetchUsers().finally(() => setLoading(false)); }, [fetchUsers]);

  const handleCreate = useCallback(async () => {
    const result = await post('/users', { displayName: form.displayName, email: form.email, temporaryPassword: form.temporaryPassword, role: 'admin' });
    if (result.success) { setShowAdd(false); setForm({ displayName: '', email: '', temporaryPassword: '' }); fetchUsers(); toast.show('User created', 'success'); }
    else setError(result.error || 'Failed to create user');
  }, [post, form, fetchUsers]);

  const handleSuspend = useCallback(async (uid: string) => {
    if (!window.confirm('Suspend this user? They will be logged out immediately.')) return;
    const result = await post(`/users/${uid}/suspend`, {});
    if (result.success) { fetchUsers(); toast.show('User suspended', 'success'); }
    else setError(result.error || 'Failed to suspend');
  }, [post, fetchUsers]);

  const handleReactivate = useCallback(async (uid: string) => {
    const result = await post(`/users/${uid}/reactivate`, {});
    if (result.success) { fetchUsers(); toast.show('User reactivated', 'success'); }
    else setError(result.error || 'Failed to reactivate');
  }, [post, fetchUsers]);

  const handleResetPassword = useCallback(async (uid: string) => {
    const result = await post<{ resetLink: string; email: string }>(`/users/${uid}/reset-password`, {});
    if (result.success && result.data) {
      toast.show(`Password reset link sent to ${result.data.email}`, 'success');
    } else setError(result.error || 'Failed to reset password');
  }, [post]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">User Management</h1>
        <button onClick={() => setShowAdd(true)}
          className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Add Admin User
        </button>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
      {toast.message && <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 text-green-700 text-sm">{toast.message}</div>}

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map(u => (
              <tr key={u.uid} className={u.status === 'suspended' ? 'bg-red-50/30' : ''}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.displayName || u.email}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${u.role === 'doctor' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {u.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{u.createdAt?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  {(() => {
                    const isSelf = u.uid === currentUser?.uid;
                    // Admin cannot manage doctor accounts
                    const adminBlockedByRole = userRole === 'admin' && u.role === 'doctor';
                    if (adminBlockedByRole) return null;

                    return (
                      <div className="flex items-center justify-end gap-1">
                        {u.status === 'active' ? (
                          <button
                            onClick={() => !isSelf && handleSuspend(u.uid)}
                            disabled={isSelf}
                            title={isSelf ? 'You cannot suspend your own account' : 'Suspend user'}
                            className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${isSelf ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
                          >
                            <ShieldOff className="h-3 w-3" /> Suspend
                          </button>
                        ) : (
                          <button onClick={() => handleReactivate(u.uid)} className="px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded flex items-center gap-1">
                            <RotateCcw className="h-3 w-3" /> Reactivate
                          </button>
                        )}
                        <button
                          onClick={() => !isSelf && handleResetPassword(u.uid)}
                          disabled={isSelf}
                          title={isSelf ? 'You cannot reset your own password here' : 'Reset password'}
                          className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${isSelf ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
                        >
                          <KeyRound className="h-3 w-3" /> Reset Password
                        </button>
                      </div>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Add Admin User</h2>
              <button onClick={() => setShowAdd(false)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                <input value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password * (min 12 chars)</label>
                <input type="password" value={form.temporaryPassword} onChange={e => setForm(f => ({ ...f, temporaryPassword: e.target.value }))} className="w-full px-3 py-2 border rounded" /></div>
            </div>
            <p className="text-xs text-gray-500 mt-3">User will be prompted to change password on first login.</p>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button onClick={handleCreate} disabled={!form.displayName || !form.email || form.temporaryPassword.length < 12}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Create</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
